/**
 * Textos (`es`) de pantalla d'Existències del BackOffice per Lot + Lloc (OBR-143, OBR-146, OBR-14): ajust, «Lot nou d'ajust»,
 * bloqueig, estats i «A regularitzar». Namespaces `backoffice.stockLine` i `backoffice.stockRegularize`. Tall propi: no es toca
 * `ca.ts`. Les tres llengües han de tenir les mateixes claus (i18n.test.ts); cap text literal a les vistes.
 */

export const stockLineEs = {
  'backoffice.stockLine': {
    filterBlocked: 'Bloqueado',
    blockedSince: 'Bloqueado el {{date}}',
    blockedReason: 'Motivo: {{reason}}',
    blockedNoReason: 'Sin motivo escrito',
    blockedHint:
      'Lotes bloqueados a mano, excepciones de recepción pendientes de decisión y devoluciones pendientes de devolver: no cuenta como disponible para producción.',

    actionAdjust: 'Ajusta',
    actionBlock: 'Bloquea',
    actionUnblock: 'Desbloquea',
    actionNewLot: 'Lote nuevo de ajuste',

    adjustBody:
      'El ajuste se hace sobre este lote en este lugar. Los movimientos no se editan nunca: queda un movimiento nuevo con el motivo escrito.',
    adjustFieldLot: 'Lote',
    adjustFieldPlace: 'Lugar',
    adjustFieldBalance: 'Existencias ahora',
    adjustNoLot: 'Sin lote',
    adjustAfter: 'Quedarán {{quantity}} {{unit}}.',
    adjustNegative:
      'No se puede dejar el saldo por debajo de 0: en este lote y lugar solo hay {{quantity}} {{unit}}.',
    adjustNoLotHint:
      'Estas existencias no tienen lote: solo se pueden sacar. Para dar de alta existencias, usa «Lote nuevo de ajuste».',
    adjustLotRequired:
      'Para añadir existencias hace falta un lote. Usa «Lote nuevo de ajuste».',
    adjustExpiry: 'Caducidad del lote',
    adjustExpiryHint: 'Este lote no tiene y el artículo la pide: hay que indicarla para añadirle existencias.',
    adjustExpiryInvalid: 'Indica una fecha de caducidad.',

    newLotTitle: 'Lote nuevo de ajuste',
    newLotBody:
      'Da de alta existencias que no tienen lote: se crea un lote interno nuevo y queda un movimiento de ajuste con el motivo escrito.',
    newLotSupplierCode: 'Código de lote del proveedor',
    newLotSupplierCodeHint: 'Opcional: el que lleva el envase.',
    newLotExpiry: 'Caducidad',
    newLotExpiryRequired: 'Obligatoria para este artículo.',
    newLotExpiryOptional: 'Opcional para este artículo.',
    newLotExpiryInvalid: 'Indica la fecha de caducidad.',
    newLotPlace: 'Lugar',
    newLotPlacePlaceholder: 'Elige dónde están',
    newLotQuantity: 'Cantidad',
    newLotQuantityHint: 'Mayor que cero, en {{unit}}.',
    newLotQuantityInvalid: 'Tiene que ser mayor que cero.',
    newLotQuantityTooMuch: 'No puede pasar de las {{quantity}} {{unit}} que hay sin lote.',
    newLotReason: 'Motivo',
    newLotConfirm: 'Crea el lote',
    newLotCreating: 'Creando…',
    newLotProduction:
      'Este artículo se produce: su lote lo genera la orden de trabajo al cerrarla y no se puede crear a mano. Si las existencias no cuadran, ajusta un lote que ya exista.',
    newLotNoStock:
      'Este artículo no tiene control de existencias: no hay existencias que dar de alta.',
    noDefaultPlace: 'Sin lugar por defecto',
    noDefaultPlaceHint:
      'Este artículo no tiene lugar por defecto. Elige uno o las existencias quedarán «Sin lugar» y habrá que regularizarlas.',
    defaultPlaceHint: 'Lugar por defecto del artículo.',
    defaultPlaceUnavailableHint:
      'El lugar por defecto del artículo ya no admite existencias. Elige otro o quedarán «Sin lugar».',
    // Aviso al elegir un destino donde el lote ya está bloqueado (no bloquea el movimiento).
    destinationBlocked_one: 'El destino tiene este lote bloqueado: lo que muevas allí quedará bloqueado.',
    destinationBlocked_other:
      'El destino tiene {{count}} de estos lotes bloqueados: lo que muevas allí quedará bloqueado.',

    blockTitle: 'Bloquea el lote',
    unblockTitle: 'Desbloquea el lote',
    blockBody:
      'Un lote bloqueado no se puede consumir ni expedir y la app de planta no lo propone. Las existencias siguen ahí físicamente y puedes desbloquearlo cuando quieras.',
    unblockBody: 'El lote volverá a estar disponible para consumir y expedir.',
    stateReason: 'Motivo',
    stateReasonPlaceholder: 'Opcional: por qué se bloquea o se desbloquea',
    blockConfirm: 'Bloquea',
    unblockConfirm: 'Desbloquea',
    stateWorking: 'Guardando…',

    errNegative:
      'No se puede dejar el saldo por debajo de 0. Las existencias de este lote han cambiado: ábrelo de nuevo para ver lo que queda.',
    errLotRequired: 'Para añadir existencias hace falta un lote. Usa «Lote nuevo de ajuste».',
    errExpiryRequired: 'Este artículo pide caducidad: indícala.',
    errForbidden: 'No tienes permiso para ajustar existencias.',
    errBadPlace: 'Este lugar ya no admite existencias. Elige otro.',
    errWorkOrder:
      'Este artículo se produce: su lote lo crea una orden de trabajo al cerrarla y no se puede dar de alta a mano.',
    errStateForbidden:
      'Solo administración, responsable de producción y calidad pueden bloquear o desbloquear.',
    errGeneric: 'No se ha podido guardar. Vuelve a intentarlo.',
  },

  'backoffice.stockRegularize': {
    tab: 'A regularizar',
    tabsLabel: 'Vista de existencias',
    tabStock: 'Existencias',
    title: 'Existencias por regularizar',
    subtitle:
      'Existencias que no se pueden trazar o que no cuadran: sin lote, en negativo, sin caducidad o sin lugar.',
    filterKind: 'Problema',
    kindAll: 'Todos',
    kind_no_lot: 'Sin lote',
    kind_negative: 'Saldo negativo',
    kind_no_expiry: 'Sin caducidad',
    kind_no_place: 'Sin lugar',
    hint_no_lot: 'No pertenecen a ningún lote: no son trazables ni se pueden expedir.',
    hint_negative: 'Se ha gastado más de lo que constaba. Ajusta el saldo con el motivo escrito.',
    hint_no_expiry: 'El lote no tiene fecha de caducidad y el artículo la pide.',
    hint_no_place: 'No tienen lugar: no se sabe dónde están. Ponlas donde toca.',
    colArticle: 'Artículo',
    colLot: 'Lote',
    colPlace: 'Lugar',
    colQuantity: 'Cantidad',
    colProblem: 'Problema',
    empty: 'Todo en orden: no hay existencias por regularizar.',
    emptyFiltered: 'Ninguna línea coincide con el filtro.',
    loadError: 'No se ha podido cargar la lista.',
    actionAssignLot: 'Asigna un lote',
    actionAdjustZero: 'Ajusta a cero',
    actionSetExpiry: 'Pon la caducidad',
    actionPlace: 'Ponlas en un lugar',
    noActionHint: 'Esta línea se regulariza desde la app de planta o con un lote de ajuste.',
    assignTitle: 'Asigna un lote nuevo',
    assignBody:
      'Se crea un lote interno con la caducidad que indiques y se le pasan estas existencias; la línea sin lote queda a cero.',
    assignPartial:
      'Se ha creado el lote, pero no se ha podido dejar a cero la línea sin lote. Ajústala a cero desde esta lista.',
    zeroTitle: 'Ajusta a cero',
    zeroNote: 'Regularización: línea ajustada a cero.',
    assignNote: 'Regularización: existencias sin lote pasadas a un lote nuevo.',
    expiryTitle: 'Pon la caducidad del lote',
    expiryBody:
      'Solo cambia la fecha de caducidad del lote; las existencias y los movimientos no se tocan.',
    expiryLabel: 'Caducidad',
    expiryConfirm: 'Guarda la caducidad',
    expiryInvalid: 'Indica la fecha de caducidad.',
    expiryForbidden: 'No tienes permiso para cambiar la caducidad de este lote.',
    expiryError: 'No se ha podido guardar la caducidad.',
    expiryWorking: 'Guardando…',
  },
} as const;
